import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { fetchCatchBoardDeals } from 'src/utils/catchBoardApi'
import {
  buildCatchBoardListQueryParams,
  groupCatchBoardDealsByStage,
  mapCatchBoardColumnTotals,
  mapCatchBoardListResponse,
} from 'src/utils/catchBoardMappers'
import {
  CATCH_BOARD_DEFAULT_OWNER_FILTER,
  CATCH_BOARD_DEFAULT_READINESS_FILTER,
  CATCH_BOARD_OWNER_FILTER,
} from './catchBoardConstants'

export const EMPTY_CATCH_BOARD_STATE = Object.freeze({
  deals: [],
  columns: groupCatchBoardDealsByStage([]),
  filters: {},
  totals: {
    dealCount: 0,
    dealValuePence: 0,
    potentialDealValuePence: 0,
    dealValueLabel: '—',
    potentialDealValueLabel: '—',
    momentum: null,
  },
  columnTotalsByStage: mapCatchBoardColumnTotals({}),
})

export const DEFAULT_CATCH_BOARD_FILTERS = Object.freeze({
  ownerFilter: CATCH_BOARD_DEFAULT_OWNER_FILTER,
  readinessFilter: CATCH_BOARD_DEFAULT_READINESS_FILTER,
  search: '',
  hideClosed: false,
})

/**
 * Fetch Catch Board deals for the current filter state.
 * @param {{ ownerFilter?: string, readinessFilter?: string, search?: string, hideClosed?: boolean }} filters
 */
export function useCatchBoardData(filters = DEFAULT_CATCH_BOARD_FILTERS) {
  const [board, setBoard] = useState(EMPTY_CATCH_BOARD_STATE)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reloadToken, setReloadToken] = useState(0)
  const hasLoadedRef = useRef(false)

  const queryParams = useMemo(
    () =>
      buildCatchBoardListQueryParams({
        ownerFilter: filters.ownerFilter || CATCH_BOARD_OWNER_FILTER.MINE,
        readinessFilter:
          filters.readinessFilter || CATCH_BOARD_DEFAULT_READINESS_FILTER,
        search: filters.search || '',
        hideClosed: Boolean(filters.hideClosed),
      }),
    [
      filters.ownerFilter,
      filters.readinessFilter,
      filters.search,
      filters.hideClosed,
    ],
  )

  const queryKey = useMemo(() => JSON.stringify(queryParams), [queryParams])

  const refresh = useCallback(() => {
    setReloadToken(token => token + 1)
  }, [])

  useEffect(() => {
    let cancelled = false

    // Drop stale WoW colours immediately when filters/query change.
    setBoard(prev => {
      if (!prev?.totals?.momentum) {
        return prev
      }
      return {
        ...prev,
        totals: {
          ...prev.totals,
          momentum: null,
        },
      }
    })
    setLoading(true)
    setError(null)

    async function loadDeals() {
      try {
        const data = await fetchCatchBoardDeals(queryParams)
        if (cancelled) {
          return
        }

        setBoard(mapCatchBoardListResponse(data))
        hasLoadedRef.current = true
      } catch (err) {
        if (cancelled) {
          return
        }

        setError(err.message || 'Failed to load Catch Board deals')
        if (!hasLoadedRef.current) {
          setBoard(EMPTY_CATCH_BOARD_STATE)
        } else {
          // Keep deals visible after a failed refresh, but never keep stale momentum.
          setBoard(prev => ({
            ...prev,
            totals: {
              ...prev.totals,
              momentum: null,
            },
          }))
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadDeals()

    return () => {
      cancelled = true
    }
  }, [queryKey, queryParams, reloadToken])

  return {
    board,
    setBoard,
    loading,
    error,
    queryParams,
    refresh,
  }
}
